import {
  AnnotationIcon,
  ChatAltIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  LightBulbIcon,
} from "@heroicons/react/outline";
import {
  Calendar,
  Target05,
  Trophy01,
  Upload04,
} from "@untitled-ui/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";
import { ComponentType, ReactNode, SVGProps, useEffect, useState } from "react";
import {
  ActionLabel,
  CoachingDashboardItemNode,
  CoachingDimensionNode,
  CoachingEvidenceNode,
  CoachingInsightNode,
} from "types/graphql-schema";

import AddTopicToMeetingDialog from "@apps/add-topic-to-meeting-dialog/add-topic-to-meeting-dialog";
import useLabel, { UseLabelType, getLabel } from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";

import { DimensionIcon, StatusBadge } from "./coaching-components";
import { ACTION_LABEL_DISPLAY, priorityBadge } from "./coaching-constants";
import type { ExpandOverride, Priority } from "./coaching-constants";

const URL_SOURCE_TYPES: Array<{
  match: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  getLabel: (label: UseLabelType) => string;
}> = [
  { match: "/meeting", icon: Calendar, getLabel: () => "View meeting" },
  { match: "/goal", icon: Target05, getLabel: (l) => `View ${l("goal")}` },
  {
    match: "/recognition",
    icon: Trophy01,
    getLabel: (l) => `View ${l("recognition")}`,
  },
  { match: "/checkin", icon: AnnotationIcon, getLabel: () => "View check-in" },
  {
    match: "/work-history",
    icon: DocumentTextIcon,
    getLabel: () => "View work history",
  },
  {
    match: "/work_history",
    icon: DocumentTextIcon,
    getLabel: () => "View work history",
  },
  {
    match: "/review",
    icon: Upload04,
    getLabel: (l) => `View ${l("review")}`,
  },
  { match: "/feedback", icon: Upload04, getLabel: () => "View feedback" },
  {
    match: "/assessment",
    icon: AnnotationIcon,
    getLabel: (l) => `View ${l("review")}`,
  },
  { match: "/note", icon: DocumentTextIcon, getLabel: () => "View note" },
  { match: "/survey", icon: AnnotationIcon, getLabel: () => "View survey" },
];

const getSourceTypeFromUrl = (url: string, label: UseLabelType) => {
  for (const type of URL_SOURCE_TYPES) {
    if (url.includes(type.match)) {
      return { icon: type.icon, label: type.getLabel(label) };
    }
  }
  return null;
};

const getSourceType = (label: UseLabelType, ev: CoachingEvidenceNode) => {
  const urlMatch = getSourceTypeFromUrl(ev.sourceUrl ?? "", label);
  if (urlMatch) return urlMatch;
  const text = (ev.text ?? "").toLowerCase();
  if (
    text.includes("meeting") ||
    text.includes("1-on-1") ||
    text.includes("1:1")
  )
    return { icon: Calendar, label: "View meeting" };
  if (
    text.includes("goal") ||
    text.includes("objective") ||
    text.includes("okr")
  )
    return { icon: Target05, label: `View ${label("goal")}` };
  if (
    text.includes("recognition") ||
    text.includes("kudos") ||
    text.includes("shout")
  )
    return { icon: Trophy01, label: `View ${label("recognition")}` };
  if (text.includes("check-in") || text.includes("checkin"))
    return { icon: AnnotationIcon, label: "View check-in" };
  if (text.includes("feedback") || text.includes("review"))
    return { icon: Upload04, label: "View feedback" };
  if (
    text.includes("summary") ||
    text.includes("update") ||
    text.includes("report")
  )
    return { icon: DocumentTextIcon, label: "View summary" };
  if (
    text.includes("action") ||
    text.includes("suggest") ||
    text.includes("recommend")
  )
    return { icon: LightBulbIcon, label: "View suggestion" };
  if (
    text.includes("schedule") ||
    text.includes("agenda") ||
    text.includes("calendar")
  )
    return { icon: Calendar, label: "View event" };

  return { icon: ChatAltIcon, label: "View source" };
};

export const InsightCardData = ({
  isOpen,
  insight,
  actionButton,
}: {
  isOpen: boolean;
  insight: CoachingInsightNode;
  actionButton?: ReactNode;
}) => {
  const label = getLabel();
  const getEvidenceIcon = (ev: CoachingEvidenceNode) => {
    return getSourceType(label, ev).icon;
  };
  const getSourceLabel = (ev: CoachingEvidenceNode) => {
    return getSourceType(label, ev).label;
  };

  const filteredEvidence = (insight.evidence ?? []).filter(
    (ev): ev is CoachingEvidenceNode => ev != null,
  );

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="overflow-hidden"
        >
          <div className="flex">
            <div className="pl-5 pr-7 pt-1.5 pb-5 shrink-0 flex">
              <div className="w-px h-full bg-gray-200" />
            </div>
            <div className="flex-1 min-w-0 pb-5 pt-1.5 pr-3 flex flex-col gap-5">
              {/* Context section */}
              <div className="flex flex-col gap-[6px]">
                <h5 className="tf-header-sm">Context</h5>
                <p className="tf-body-sm leading-[1.4rem]">{insight.body}</p>
              </div>

              {/* Action section */}
              {insight.action && (
                <div className="flex flex-col gap-[6px]">
                  <h5 className="tf-header-sm">
                    {insight.action.label
                      ? (ACTION_LABEL_DISPLAY[
                          insight.action.label as ActionLabel
                        ] ?? insight.action.label)
                      : "Action"}
                  </h5>
                  <p className="tf-body-sm leading-[1.4rem]">
                    {insight.action.text}
                  </p>
                </div>
              )}

              {/* Evidence section */}
              {filteredEvidence.length > 0 && (
                <div className="flex flex-col gap-[6px]">
                  <h5 className="tf-header-sm">Evidence</h5>
                  <div className="flex flex-col gap-3">
                    {filteredEvidence.map((ev, i) => {
                      const Icon = getEvidenceIcon(ev);
                      return (
                        <div key={i} className="flex items-start gap-2">
                          <Icon className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                          <p className="tf-body-sm leading-[1.4rem]">
                            {ev.text}
                            {ev.sourceUrl && (
                              <>
                                {" "}
                                <AppLink
                                  to={ev.sourceUrl}
                                  className="text-blue-600 hover:text-blue-700 hover:underline"
                                >
                                  {getSourceLabel(ev)}
                                </AppLink>
                              </>
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {actionButton && <div>{actionButton}</div>}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const InsightCard = ({
  insight,
  expandOverride,
  profileId,
}: {
  insight: CoachingInsightNode;
  expandOverride: ExpandOverride;
  profileId: number;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAddTopicDialog, setShowAddTopicDialog] = useState(false);
  const currentUser = currentUserVar();
  const label = useLabel();

  useEffect(() => {
    if (expandOverride) {
      setIsOpen(expandOverride.expanded);
    }
  }, [expandOverride?.version]);
  const badge = priorityBadge[(insight.priority ?? "low") as Priority];

  const topicNotes = (insight.topics?.notes ?? []).filter(
    (n): n is string => n !== null,
  );

  return (
    <div className="rounded text-sm">
      <button
        type="button"
        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer w-full text-left rounded-[6px] hover:bg-gray-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <TopicflowIcon type="ai" className="w-4 h-4 text-violet-500 shrink-0" />
        <h4 className="tf-header-sm flex-1">{insight.title}</h4>
        <div className="flex items-center gap-2 shrink-0">
          {badge && <StatusBadge status={badge.status} label={badge.label} />}
          <Button
            theme={buttonTheme.iconGray}
            size={ButtonSize.medium}
            square
            icon={isOpen ? ChevronUpIcon : ChevronDownIcon}
          />
        </div>
      </button>

      <InsightCardData
        insight={insight}
        isOpen={isOpen}
        actionButton={
          <Button
            theme={buttonTheme.primary}
            size={ButtonSize.medium}
            onClick={() => setShowAddTopicDialog(true)}
          >
            Add as topic in {label("1-on-1")}
          </Button>
        }
      />

      {showAddTopicDialog && (
        <AddTopicToMeetingDialog
          variables={{
            startDatetimeAfter: moment().startOf("hour").format(),
            participants: [currentUser.id, profileId],
            isFormalOneonone: true,
          }}
          topicTitle={insight.topics?.title || insight.title || ""}
          topicNotes={topicNotes}
          onClose={() => setShowAddTopicDialog(false)}
          onCreatedTopic={() => setShowAddTopicDialog(false)}
        />
      )}
    </div>
  );
};

export const DimensionSection = ({
  dimension,
  dashboardCards,
  expandOverride,
  profileId,
}: {
  dimension: CoachingDimensionNode;
  dashboardCards: CoachingDashboardItemNode[];
  expandOverride: ExpandOverride;
  profileId: number;
}) => {
  const dashboardDetail = dashboardCards.find(
    (card) => card.dimension === dimension.name,
  )?.detail;

  return (
    <div className="flex flex-col gap-1 bg-white border border-gray-200 rounded-[6px] px-2 py-4">
      <div className="flex flex-col gap-0.5 mb-1 px-3">
        <div className="flex items-center gap-2.5">
          <DimensionIcon
            name={dimension.name ?? ""}
            className="h-4 w-4 text-gray-500"
          />
          <h3 className="tf-header-base-dark">{dimension.name}</h3>
        </div>
        {dashboardDetail && (
          <p className="pl-[26px] text-sm text-gray-500">{dashboardDetail}</p>
        )}
      </div>
      <div className="flex flex-col gap-0 pt-1">
        {(dimension.insights ?? [])
          .filter((insight): insight is CoachingInsightNode => insight != null)
          .sort((a, b) => {
            const order: Record<string, number> = {
              high: 0,
              medium: 1,
              low: 2,
              positive: 3,
            };
            return (
              (order[a.priority ?? "positive"] ?? 4) -
              (order[b.priority ?? "positive"] ?? 4)
            );
          })
          .map((insight, i) => (
            <InsightCard
              key={insight.title ?? i}
              insight={insight}
              expandOverride={expandOverride}
              profileId={profileId}
            />
          ))}
      </div>
    </div>
  );
};
